import type { AiSuggestionsRegenerationTrigger } from '@atlaskit/editor-common/analytics';
import { AiSuggestionsRegenerationTriggerValue } from '@atlaskit/editor-common/analytics/ai-suggestions-regeneration';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import { type AdfNode, hashNode } from '@atlassian/adf-node-hasher';

import type { AiSuggestionsPlugin, Suggestion } from '../../aiSuggestionsPluginType';
import { aiSuggestionsPluginKey } from '../../pm-plugins/main';
import { startRegenerateSuggestionsAnalytics } from './analytics';
import { regenerateSuggestions } from './api';

/** Flags the ids of suggestions currently being regenerated. */
function setRegeneratingSuggestionIds(
	api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
	ids: Set<string>,
): void {
	api?.core?.actions.execute(({ tr }) => {
		tr.setMeta(aiSuggestionsPluginKey, { action: 'SET_REGENERATING', ids });
		return tr;
	});
}

/** Every localId referenced by any suggestion's `nodeHashes`, deduped. */
export function collectTrackedLocalIds(suggestions: Suggestion[]): Set<string> {
	const trackedLocalIds = new Set<string>();
	for (const suggestion of suggestions) {
		for (const localId of Object.keys(suggestion.nodeHashes ?? {})) {
			trackedLocalIds.add(localId);
		}
	}
	return trackedLocalIds;
}

/** Resolves each tracked localId to its current ADF node in one document traversal. */
export function resolveNodesByLocalId(
	doc: PMNode,
	trackedLocalIds: Set<string>,
): Map<string, AdfNode> {
	const remaining = new Set(trackedLocalIds);
	const nodesByLocalId = new Map<string, AdfNode>();
	doc.descendants((node) => {
		if (remaining.size === 0) {
			return false;
		}
		const localId: string | undefined = node.attrs?.localId;
		if (localId && remaining.delete(localId)) {
			nodesByLocalId.set(localId, node.toJSON() as AdfNode);
		}
		return true;
	});
	return nodesByLocalId;
}

/** Hashes every resolved node in parallel */
export async function hashNodesByLocalId(
	nodesByLocalId: Map<string, AdfNode>,
): Promise<Map<string, string>> {
	const currentHashByLocalId = new Map<string, string>();
	await Promise.all(
		Array.from(nodesByLocalId, async ([localId, node]) => {
			currentHashByLocalId.set(localId, await hashNode(node));
		}),
	);
	return currentHashByLocalId;
}

/** A suggestion is stale when any tracked node is missing or its hash changed. */
export function isSuggestionStale(
	suggestion: Suggestion,
	currentHashByLocalId: Map<string, string>,
): boolean {
	for (const [localId, expectedHash] of Object.entries(suggestion.nodeHashes ?? {})) {
		const currentHash = currentHashByLocalId.get(localId);
		if (currentHash === undefined || currentHash !== expectedHash) {
			return true;
		}
	}
	return false;
}

/**
 * Stale suggestions plus every localId they track, including unchanged ones —
 * the backend treats a localId missing from `currentNodes` as a deletion.
 */
export function findStaleSuggestions(
	suggestions: Suggestion[],
	currentHashByLocalId: Map<string, string>,
): { staleSuggestionIds: Set<string>; staleTrackedLocalIds: Set<string> } {
	const staleSuggestionIds = new Set<string>();
	const staleTrackedLocalIds = new Set<string>();
	for (const suggestion of suggestions) {
		if (isSuggestionStale(suggestion, currentHashByLocalId)) {
			staleSuggestionIds.add(suggestion.id);
			for (const localId of Object.keys(suggestion.nodeHashes ?? {})) {
				staleTrackedLocalIds.add(localId);
			}
		}
	}
	return { staleSuggestionIds, staleTrackedLocalIds };
}

/** The current ADF node for each localId that still exists in the document. */
export function collectCurrentNodes(
	localIds: Iterable<string>,
	nodesByLocalId: Map<string, AdfNode>,
): AdfNode[] {
	const nodes: AdfNode[] = [];
	for (const localId of localIds) {
		const node = nodesByLocalId.get(localId);
		if (node) {
			nodes.push(node);
		}
	}
	return nodes;
}

type StaleSuggestionsUpdaterOptions = {
	regenerationTrigger?: AiSuggestionsRegenerationTrigger;
	/**
	 * `false` (default): drop stale originals and append the regenerated ones.
	 * `true`: keep every suggestion in place, swapping stale ones for their
	 * regenerated counterpart; missing ones are left untouched.
	 */
	shouldRetainStale?: boolean;
	signal?: AbortSignal;
};

/**
 * Builds a transformer that reconciles suggestions against the current
 * document: hashes each suggestion's tracked nodes, regenerates any that are
 * stale (flagging them via `setRegeneratingSuggestionIds` while in flight),
 * and merges the reply back per `shouldRetainStale`.
 */
export const createStaleSuggestionsUpdater =
	(
		getDoc: () => PMNode | undefined,
		contentAri: string,
		api: ExtractInjectionAPI<AiSuggestionsPlugin> | undefined,
		options: StaleSuggestionsUpdaterOptions = {},
	): ((suggestions: Suggestion[]) => Promise<Suggestion[]>) =>
	async (suggestions: Suggestion[]): Promise<Suggestion[]> => {
		const {
			regenerationTrigger = AiSuggestionsRegenerationTriggerValue.SUGGESTIONS_UPDATED,
			signal,
			shouldRetainStale = false,
		} = options;

		const trackedLocalIds = collectTrackedLocalIds(suggestions);
		if (trackedLocalIds.size === 0) {
			return suggestions;
		}

		const doc = getDoc();
		if (!doc) {
			return suggestions;
		}

		const nodesByLocalId = resolveNodesByLocalId(doc, trackedLocalIds);
		const currentHashByLocalId = await hashNodesByLocalId(nodesByLocalId);

		const { staleSuggestionIds, staleTrackedLocalIds } = findStaleSuggestions(
			suggestions,
			currentHashByLocalId,
		);
		if (staleSuggestionIds.size === 0) {
			return suggestions;
		}

		const currentNodes = collectCurrentNodes(staleTrackedLocalIds, nodesByLocalId);
		const regenerationAnalytics = startRegenerateSuggestionsAnalytics(api?.analytics?.actions, {
			currentNodeCount: currentNodes.length,
			staleSuggestionIds,
			suggestions,
			trigger: regenerationTrigger,
		});
		setRegeneratingSuggestionIds(api, staleSuggestionIds);

		let regenerated: Suggestion[];
		try {
			regenerated = await regenerateSuggestions({
				suggestionIds: Array.from(staleSuggestionIds),
				contentAri,
				currentNodes,
				signal,
			});
		} catch (error) {
			// On error, keep the suggestions unchanged
			regenerationAnalytics.errored(error);
			setRegeneratingSuggestionIds(api, new Set());
			return suggestions;
		}

		const regeneratedById = new Map(regenerated.map((suggestion) => [suggestion.id, suggestion]));
		setRegeneratingSuggestionIds(api, new Set());
		regenerationAnalytics.completed({
			regeneratedSuggestions: regenerated,
			shouldRetainStale,
		});

		if (shouldRetainStale) {
			// Swap each stale suggestion for its regenerated counterpart; keep the rest.
			return suggestions.map((suggestion) => regeneratedById.get(suggestion.id) ?? suggestion);
		}

		// Drop the stale originals and append the backend's replacements.
		const freshSuggestions = suggestions.filter(
			(suggestion) => !staleSuggestionIds.has(suggestion.id),
		);
		return [...freshSuggestions, ...regenerated];
	};
